import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { MdClose, MdRefresh } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Header from '../components/header';
import AppLayout from '../components/AppLayout';
import { getBilling, getPlan } from '../logic/plans/action';
import { filterBilling } from '../logic/plans/reducer';
import { Debouncer } from '../utils/debouncer';
import { getData } from '../utils/serverHelper';
import { GETPAYMENTSTATUS } from '../utils/urls';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

const SubscriptionBilling = () => {
  const dispatch = useDispatch();
  const selector = useSelector((state) => state.plans);
  const selectorAuth = useSelector((state) => state.auth);
  const isSuperAdmin = selectorAuth?.user?.role === 'super_admin';

  const [orderObj, setOrderObj] = useState({
    orderNo: '',
    refNo: '',
    orderAmt: 0,
    orderStatus: '',
  });

  const db = useMemo(() => new Debouncer(300), []);

  useEffect(() => {
    getBilling(dispatch);
    getPlan(dispatch);
  }, [dispatch]);

  const billings = useMemo(() => {
    if (!isSuperAdmin && selectorAuth?.user?.business_id) {
      return (selector.billings || []).filter(
        (b) => b.business_id === selectorAuth.user.business_id
      );
    }
    return selector.billings || [];
  }, [selector.billings, isSuperAdmin, selectorAuth?.user?.business_id]);

  const searchFilter = (value) => {
    db.debounce(() => {
      if (!value) {
        getBilling(dispatch);
      } else {
        dispatch(filterBilling(value));
      }
    });
  };

  const getStatus = async (id, txn_id) => {
    try {
      if (!txn_id || txn_id === 'txn_id') {
        setOrderObj({
          orderNo: id,
          refNo: 'txn_id',
          orderAmt: 0,
          orderStatus: 'Shipped',
        });
        return;
      }
      const data = await getData(GETPAYMENTSTATUS(id, txn_id));
      const order = data?.data?.Order_Status_Result;
      setOrderObj({
        orderNo: order?.order_no,
        refNo: order?.reference_no,
        orderAmt: order?.order_amt,
        orderStatus: order?.order_status,
      });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <AppLayout>
      {orderObj.orderNo !== '' && (
        <div className="w-screen h-screen fixed top-0 left-0 bg-black/60 z-50 flex items-center justify-center">
          <div className="w-[90%] lg:w-[50%] bg-white rounded-lg p-8 relative">
            <MdClose
              className="absolute top-6 right-6 cursor-pointer"
              onClick={() =>
                setOrderObj({ orderNo: '', refNo: '', orderAmt: 0, orderStatus: '' })
              }
            />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Payment status
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Order id</Typography>
                <Typography>{orderObj.orderNo}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Reference no</Typography>
                <Typography>{orderObj.refNo}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Status</Typography>
                <Typography>{orderObj.orderStatus}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Amount</Typography>
                <Typography>₹{orderObj.orderAmt}</Typography>
              </Box>
            </Box>
          </div>
        </div>
      )}

      <div className="w-full h-full flex flex-col gap-4 p-4">
        <Header
          title="Subscription Billing"
          actions={
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!isSuperAdmin && (
                <Button
                  component={Link}
                  to="/admin/pricePlan"
                  variant="contained"
                  sx={{ textTransform: 'none' }}
                >
                  Upgrade Plan
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<MdRefresh />}
                onClick={() => getBilling(dispatch)}
                sx={{ textTransform: 'none' }}
              >
                Refresh
              </Button>
            </Box>
          }
        />

        {isSuperAdmin && (
          <TextField
            size="small"
            placeholder="Search by business name"
            onChange={(e) => searchFilter(e.target.value)}
            sx={{ maxWidth: 320 }}
          />
        )}

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {isSuperAdmin && <TableCell>Business</TableCell>}
                <TableCell>Plan</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Final</TableCell>
                <TableCell>Txn ID</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {billings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 9 : 8} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      No subscription payments yet.
                      {!isSuperAdmin && (
                        <>
                          {' '}
                          <Link to="/admin/pricePlan">Choose a plan</Link>
                        </>
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                billings.map((row) => (
                  <TableRow key={row.id}>
                    {isSuperAdmin && <TableCell>{row.business_name}</TableCell>}
                    <TableCell>{row.plan_name}</TableCell>
                    <TableCell>{row.month_count} mo</TableCell>
                    <TableCell align="right">₹{row.month_price}</TableCell>
                    <TableCell align="right">₹{parseFloat(row.final_amount || 0).toFixed(2)}</TableCell>
                    <TableCell>{row.txn_id || '—'}</TableCell>
                    <TableCell>{moment(row.purchase_date).format('DD-MM-YYYY')}</TableCell>
                    <TableCell>{row.status || '—'}</TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        onClick={() => getStatus(row.id, row.txn_id)}
                        sx={{ textTransform: 'none' }}
                      >
                        Status
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </AppLayout>
  );
};

export default SubscriptionBilling;
